import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuery } from 'react-query';
import toast from 'react-hot-toast';
import {
  FunnelIcon,
  AdjustmentsHorizontalIcon,
  Squares2X2Icon,
  ListBulletIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  XMarkIcon,
  ShoppingBagIcon,
  MagnifyingGlassIcon,
  ChevronUpIcon,
  ArrowsUpDownIcon,
} from '@heroicons/react/24/outline';
import { productsAPI, categoriesAPI, brandsAPI } from '../services/api';
import ProductCard from '../components/products/ProductCard';
import ProductFilters from '../components/products/ProductFilters';
import HorizontalProductFilters from '../components/products/HorizontalProductFilters';
import ProductSort from '../components/products/ProductSort';
import Pagination from '../components/common/Pagination';
import CatalogPreviewModal from '../components/products/CatalogPreviewModal';
import { exportCatalogPdf } from '../utils/exportCatalogPdf';
import StorePageHero from '../components/layout/StorePageHero';

const Products = ({ layout = 'sidebar' }) => {
  const isShortlist = layout === 'horizontal';
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewMode, setViewMode] = useState('grid');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState({});
  const [showCatalogPreview, setShowCatalogPreview] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Get URL parameters
  const category = searchParams.get('category') || '';
  const selectedBrands = (searchParams.get('brand') || '')
    .split(',')
    .map((name) => name.trim())
    .filter(Boolean);
  const brand = selectedBrands.join(',');
  const minPrice = searchParams.get('minPrice') || '';
  const maxPrice = searchParams.get('maxPrice') || '';
  const sortBy = searchParams.get('sortBy') || 'createdAt';
  const sortOrder = searchParams.get('sortOrder') || 'desc';
  const page = parseInt(searchParams.get('page')) || 1;
  const pharmaOnly = searchParams.get('pharma') === 'true';
  const lifestyleOnly = searchParams.get('lifestyle') === 'true';

  const preserveCategoryParams = (params) => {
    if (pharmaOnly) params.pharma = 'true';
    if (lifestyleOnly) params.lifestyle = 'true';
    return params;
  };

  const categoryHomePath = pharmaOnly
    ? '/home?pharma=true'
    : lifestyleOnly
      ? '/home?lifestyle=true'
      : '/home';

  // Fetch products with filters
  const { data: productsResponse, isLoading, error } = useQuery(
    ['products', { category, brand, minPrice, maxPrice, sortBy, sortOrder, page, pharmaOnly, lifestyleOnly }],
    () => productsAPI.getAll({ category, brand, minPrice, maxPrice, sortBy, sortOrder, page, pharmaOnly, lifestyleOnly }),
    {
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );
  const productsData = productsResponse?.data;

  // Fetch categories and brands for filters
  const { data: categoriesResponse, isLoading: categoriesLoading } = useQuery('categories', categoriesAPI.getAll, {
    staleTime: 10 * 60 * 1000, // 10 minutes
    retry: 3,
    retryDelay: 1000
  });
  const { data: brandsResponse, isLoading: brandsLoading } = useQuery('brands', brandsAPI.getAll, {
    staleTime: 10 * 60 * 1000, // 10 minutes
    retry: 3,
    retryDelay: 1000
  });
  const categories = categoriesResponse?.data || [];
  const brands = brandsResponse?.data || [];

  const handleFilterChange = (filterType, value) => {
    const current = Object.fromEntries(searchParams.entries());
    if (value) {
      current[filterType] = value;
    } else {
      delete current[filterType];
    }
    preserveCategoryParams(current);
    current.page = '1'; // Reset to page 1
    setSearchParams(current);
  };

  const handleBrandFilterChange = (brands) => {
    const current = Object.fromEntries(searchParams.entries());
    if (brands.length > 0) {
      current.brand = brands.join(',');
    } else {
      delete current.brand;
    }
    preserveCategoryParams(current);
    current.page = '1';
    setSearchParams(current);
  };

  const handleSortChange = (newSortBy, newSortOrder) => {
    const current = Object.fromEntries(searchParams.entries());
    current.sortBy = newSortBy;
    current.sortOrder = newSortOrder;
    current.page = '1';
    preserveCategoryParams(current);
    setSearchParams(current);
  };

  const handlePageChange = (newPage) => {
    const current = Object.fromEntries(searchParams.entries());
    current.page = String(newPage);
    preserveCategoryParams(current);
    setSearchParams(current);
  };

  const clearAllFilters = () => {
    if (pharmaOnly) {
      setSearchParams({ pharma: 'true' });
    } else if (lifestyleOnly) {
      setSearchParams({ lifestyle: 'true' });
    } else {
      setSearchParams({});
    }
  };

  const selectedList = Object.values(selectedProducts);
  const selectedCount = selectedList.length;

  const handleToggleSelect = (product) => {
    setSelectedProducts((prev) => {
      if (prev[product.id]) {
        const next = { ...prev };
        delete next[product.id];
        return next;
      }
      return { ...prev, [product.id]: product };
    });
  };

  const handleClearSelection = () => {
    setSelectedProducts({});
    setShowCatalogPreview(false);
  };

  const handleExportCatalog = async () => {
    if (!selectedCount || isExporting) {
      return;
    }

    setIsExporting(true);
    try {
      const filename = await exportCatalogPdf(selectedList);
      toast.success(`Downloaded ${filename}`);
    } catch (exportError) {
      console.error('Error exporting catalog PDF:', exportError);
      toast.error('Could not export the catalog PDF. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const activeFilters = [
    category && { type: 'category', label: category },
    ...selectedBrands.map((brandName) => ({
      type: 'brand',
      label: brandName,
      value: brandName,
    })),
    (minPrice || maxPrice) && {
      type: 'price',
      label: `₹${minPrice || '0'} – ₹${maxPrice || '∞'}`,
    },
  ].filter(Boolean);

  const removeFilter = (filterType, value) => {
    if (filterType === 'price') {
      handleFilterChange('minPrice', '');
      handleFilterChange('maxPrice', '');
    } else if (filterType === 'brand' && value) {
      handleBrandFilterChange(selectedBrands.filter((brandName) => brandName !== value));
    } else {
      handleFilterChange(filterType, '');
    }
  };

  const pageTitle = pharmaOnly
    ? 'Pharma Products'
    : lifestyleOnly
      ? 'Lifestyle Products'
    : selectedBrands.length === 1
      ? `${selectedBrands[0]} Products`
      : selectedBrands.length > 1
        ? 'Filtered Products'
      : category
        ? `${category} Products`
        : 'All Products';

  const SortCard = () => (
    <div className="store-filter-panel">
      <h3 className="text-base font-display font-bold text-slate-900 flex items-center gap-2 mb-4">
        <ArrowsUpDownIcon className="w-5 h-5 text-primary-600" />
        Sort
      </h3>
      <ProductSort
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortChange={handleSortChange}
        fullWidth
      />
    </div>
  );

  const ViewModeToggle = () => (
    <div className="store-view-toggle">
      <button
        type="button"
        onClick={() => setViewMode('grid')}
        className={`store-view-btn ${viewMode === 'grid' ? 'store-view-btn-active' : 'store-view-btn-inactive'}`}
        aria-label="Grid view"
      >
        <Squares2X2Icon className="w-4 h-4 sm:w-5 sm:h-5" />
      </button>
      <button
        type="button"
        onClick={() => setViewMode('list')}
        className={`store-view-btn ${viewMode === 'list' ? 'store-view-btn-active' : 'store-view-btn-inactive'}`}
        aria-label="List view"
      >
        <ListBulletIcon className="w-4 h-4 sm:w-5 sm:h-5" />
      </button>
    </div>
  );

  const filterChipsFooter = activeFilters.length > 0 ? (
    <div className="mt-5 flex flex-wrap items-center gap-2">
      <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 mr-1">Filters</span>
      {activeFilters.map((filter) => (
        <span key={filter.type === 'brand' ? `brand-${filter.value}` : filter.type} className="store-chip">
          {filter.label}
          <button
            type="button"
            onClick={() => removeFilter(filter.type, filter.value)}
            className="p-0.5 rounded-full hover:bg-primary-100 transition-colors"
            aria-label={`Remove ${filter.type} filter`}
          >
            <XMarkIcon className="w-3.5 h-3.5" />
          </button>
        </span>
      ))}
      <button type="button" onClick={clearAllFilters} className="text-sm font-medium text-primary-600 hover:text-primary-800 ml-1">
        Clear all
      </button>
    </div>
  ) : null;

  return (
    <div className="store-page">
      {pharmaOnly && (
        <div className="store-pharma-banner">
          <span className="store-pharma-banner-text">Viewing Pharma Products</span>
        </div>
      )}
      {lifestyleOnly && (
        <div className="store-lifestyle-banner">
          <span className="store-lifestyle-banner-text">Viewing Lifestyle Products</span>
        </div>
      )}

      {isShortlist ? (
        <StorePageHero
          eyebrow="Catalog Builder"
          title="Product Shortlist"
          subtitle="Curate products for your clients. Select items, preview the catalog, and export a professional PDF."
        >
          <div className="flex gap-2 sm:gap-3 w-full sm:w-auto">
            <div className="store-stat-card flex-1 sm:flex-none">
              <p className="store-stat-label">Available</p>
              <p className="store-stat-value">{productsData?.length || 0}</p>
            </div>
            <div className={`store-stat-card flex-1 sm:flex-none ${selectedCount > 0 ? 'store-stat-card-active' : ''}`}>
              <p className="store-stat-label">Selected</p>
              <p className={`store-stat-value ${selectedCount > 0 ? 'text-primary-700' : ''}`}>{selectedCount}</p>
            </div>
          </div>
        </StorePageHero>
      ) : (
        <StorePageHero
          eyebrow="Product Catalog"
          title={pageTitle}
          subtitle={isLoading ? 'Loading products...' : `${productsData?.length || 0} products available`}
          footer={filterChipsFooter}
        >
          <ViewModeToggle />
          <button
            type="button"
            onClick={() => setShowFilters(true)}
            className="lg:hidden store-cta-primary py-2.5 px-4 text-sm"
          >
            <FunnelIcon className="w-4 h-4" />
            Filters
            {activeFilters.length > 0 && (
              <span className="bg-white/20 px-1.5 py-0.5 rounded-md text-xs">{activeFilters.length}</span>
            )}
          </button>
          <Link to={categoryHomePath} className="hidden sm:inline-flex store-cta-outline">
            <ShoppingBagIcon className="w-4 h-4" />
            Shop by Budget
          </Link>
        </StorePageHero>
      )}

      <div className={`store-content ${isShortlist && selectedCount > 0 ? 'pb-32' : ''}`}>
        {isShortlist && (
          <div className="sticky top-16 lg:top-20 z-30 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 pt-1 pb-2 sm:pt-3 sm:pb-4 mb-3 sm:mb-6">
            <HorizontalProductFilters
              categories={categories}
              brands={brands}
              selectedCategory={category}
              selectedBrand={brand}
              minPrice={minPrice}
              maxPrice={maxPrice}
              onFilterChange={handleFilterChange}
              onClearFilters={clearAllFilters}
              isLoading={categoriesLoading || brandsLoading}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSortChange={handleSortChange}
            />
          </div>
        )}

        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
          {/* Filters Sidebar — desktop */}
          {!isShortlist && (
            <aside className="hidden lg:block lg:w-72 xl:w-80 shrink-0">
              <div className="sticky top-24 space-y-4">
                <ProductFilters
                  categories={categories}
                  brands={brands}
                  selectedCategory={category}
                  selectedBrands={selectedBrands}
                  minPrice={minPrice}
                  maxPrice={maxPrice}
                  onFilterChange={handleFilterChange}
                  onBrandChange={handleBrandFilterChange}
                  onClearFilters={clearAllFilters}
                  isLoading={categoriesLoading || brandsLoading}
                />
                <SortCard />
              </div>
            </aside>
          )}

          {/* Mobile filter drawer */}
          {!isShortlist && (
            <AnimatePresence>
              {showFilters && (
                <div className="fixed inset-0 z-50 lg:hidden">
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm"
                    onClick={() => setShowFilters(false)}
                  />
                  <motion.div
                    initial={{ x: '100%' }}
                    animate={{ x: 0 }}
                    exit={{ x: '100%' }}
                    transition={{ type: 'spring', damping: 28, stiffness: 320 }}
                    className="absolute right-0 top-0 bottom-0 w-full max-w-sm bg-white shadow-2xl overflow-y-auto"
                  >
                    <div className="sticky top-0 z-10 flex items-center justify-between px-5 py-4 bg-white border-b border-slate-200">
                      <h2 className="font-display font-bold text-slate-900">Filters</h2>
                      <button
                        type="button"
                        onClick={() => setShowFilters(false)}
                        className="p-2 rounded-lg text-slate-500 hover:bg-slate-100"
                      >
                        <XMarkIcon className="w-5 h-5" />
                      </button>
                    </div>
                    <div className="p-4 space-y-4">
                      <ProductFilters
                        categories={categories}
                        brands={brands}
                        selectedCategory={category}
                        selectedBrands={selectedBrands}
                        minPrice={minPrice}
                        maxPrice={maxPrice}
                        onFilterChange={handleFilterChange}
                        onBrandChange={handleBrandFilterChange}
                        onClearFilters={clearAllFilters}
                        isLoading={categoriesLoading || brandsLoading}
                      />
                      <SortCard />
                    </div>
                    <div className="sticky bottom-0 p-4 bg-white border-t border-slate-200">
                      <button
                        type="button"
                        onClick={() => setShowFilters(false)}
                        className="w-full store-cta-primary py-3"
                      >
                        Show {productsData?.length || 0} products
                      </button>
                    </div>
                  </motion.div>
                </div>
              )}
            </AnimatePresence>
          )}

          {/* Products Grid */}
          <div className="flex-1">
            {isShortlist && (
              <div className="hidden sm:flex justify-end mb-4 sm:mb-6">
                <ViewModeToggle />
              </div>
            )}

            {/* Loading State */}
            {isLoading && (
              <div className={isShortlist ? 'grid gap-5 grid-cols-1 md:grid-cols-2 lg:grid-cols-3' : 'store-card-grid'}>
                {[...Array(isShortlist ? 9 : 9)].map((_, i) => (
                  <div key={i} className="animate-pulse bg-white border border-slate-200 rounded-2xl overflow-hidden">
                    <div className="bg-slate-100 aspect-[4/3]" />
                    <div className="p-4 space-y-3">
                      <div className="h-3 bg-slate-100 rounded-lg w-1/4" />
                      <div className="h-4 bg-slate-100 rounded-lg w-full" />
                      <div className="h-4 bg-slate-100 rounded-lg w-2/3" />
                      <div className="flex justify-between pt-3">
                        <div className="h-4 bg-slate-100 rounded w-1/4" />
                        <div className="h-5 bg-slate-100 rounded w-1/3" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="store-empty-state">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-red-50 text-red-500 mb-4">
                  <AdjustmentsHorizontalIcon className="w-7 h-7" />
                </div>
                <h3 className="text-lg font-display font-bold text-slate-900 mb-2">
                  Could not load products
                </h3>
                <p className="text-slate-600 mb-6 max-w-md mx-auto">
                  {error.message || 'Something went wrong while loading products. Please try again.'}
                </p>
                <button
                  type="button"
                  onClick={() => window.location.reload()}
                  className="store-cta-primary"
                >
                  Try Again
                </button>
              </div>
            )}

            {/* Products Grid */}
            {!isLoading && !error && productsData && (
              <>
                <div className={
                  viewMode === 'list'
                    ? 'flex flex-col gap-4'
                    : isShortlist
                      ? 'grid gap-5 grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
                      : 'store-card-grid'
                }>
                  {productsData.map((product, index) => (
                    <motion.div
                      key={product.id}
                      initial={{ opacity: 0, y: 16 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: Math.min(index * 0.04, 0.24) }}
                    >
                      <ProductCard
                        product={product}
                        viewMode={viewMode}
                        selectable={isShortlist}
                        isSelected={Boolean(selectedProducts[product.id])}
                        onToggleSelect={handleToggleSelect}
                      />
                    </motion.div>
                  ))}
                </div>

                {productsData.length === 0 && (
                  <div className="store-empty-state">
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-slate-100 text-slate-400 mb-4">
                      <MagnifyingGlassIcon className="w-7 h-7" />
                    </div>
                    <h3 className="text-lg font-display font-bold text-slate-900 mb-2">
                      No products found
                    </h3>
                    <p className="text-slate-600 mb-6 max-w-md mx-auto">
                      Try adjusting your filters or browse our full catalog from the home page.
                    </p>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                      <button
                        type="button"
                        onClick={clearAllFilters}
                        className="store-cta-primary"
                      >
                        Clear Filters
                      </button>
                      <Link
                        to={categoryHomePath}
                        className="store-cta-secondary"
                      >
                        Back to Home
                      </Link>
                    </div>
                  </div>
                )}

                {/* Pagination - Disabled for now since we're not implementing pagination */}
                {/* {productsData.pagination && productsData.pagination.totalPages > 1 && (
                  <div className="mt-12">
                    <Pagination
                      currentPage={productsData.pagination.currentPage}
                      totalPages={productsData.pagination.totalPages}
                      onPageChange={handlePageChange}
                    />
                  </div>
                )} */}
              </>
            )}
          </div>
        </div>
      </div>

      {isShortlist && selectedCount > 0 && (
        <div className="store-action-bar">
          <div className="store-container py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-primary-600 text-white font-bold text-sm">
                {selectedCount}
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-900">
                  {selectedCount} product{selectedCount === 1 ? '' : 's'} selected
                </p>
                <p className="text-xs text-slate-500">Ready to preview or export catalog</p>
              </div>
            </div>
            <div className="flex items-center justify-center sm:justify-end gap-2">
              <button type="button" onClick={() => setShowCatalogPreview(true)} className="store-cta-outline">
                <EyeIcon className="w-4 h-4" />
                Preview
              </button>
              <button type="button" onClick={handleClearSelection} className="store-cta-outline">
                <XMarkIcon className="w-4 h-4" />
                Clear
              </button>
              <button
                type="button"
                onClick={handleExportCatalog}
                disabled={isExporting}
                className="store-cta-primary py-2.5 px-5 text-sm disabled:opacity-60"
              >
                <ArrowDownTrayIcon className="w-4 h-4" />
                {isExporting ? 'Exporting...' : 'Export PDF'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCatalogPreview && (
        <CatalogPreviewModal
          products={selectedList}
          onClose={() => setShowCatalogPreview(false)}
        />
      )}

      <AnimatePresence>
        {showScrollTop && (
          <motion.button
            type="button"
            initial={{ opacity: 0, scale: 0.8, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 12 }}
            transition={{ duration: 0.2 }}
            onClick={scrollToTop}
            className={`fixed right-4 sm:right-6 z-50 flex items-center justify-center w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-primary-600 text-white shadow-lg hover:bg-primary-700 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors ${
              isShortlist && selectedCount > 0 ? 'bottom-24 sm:bottom-28' : 'bottom-4 sm:bottom-6'
            }`}
            aria-label="Scroll to top"
          >
            <ChevronUpIcon className="w-5 h-5 sm:w-6 sm:h-6" />
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Products;
